import { useMutation, useQuery } from 'react-query'

import { QueryKey } from '../../../common/api'
import { queryClient } from '../../../common/providers/query'
import { workflowApi } from '../api/workflow.api'
import { mapChecklistDto, mapChecklistSaveDto } from '../mappers/map-checklist-dto'
import { MilestoneItem, Checklist, MilestoneTransferDto, MilestoneFilterTemplateDto, FullMilestoneOrder } from '../types/workflow.types'

export const useGetUserWorkflows = () => {
  return useQuery([QueryKey.Milestones], async () => {
    const response = await workflowApi.getUserActivitiesAndMilestones()

    return response.data
  })
}

export const useGetMilestone = (id: number) => {
  return useQuery([QueryKey.Milestone], async () => {
    try {
      return await workflowApi.getMilestone(id)
    } catch {
      return null
    }
  })
}

export const useCreateMilestone = () => {
  return useMutation(
    async (milestone: Partial<MilestoneItem>) => {
      const response = await workflowApi.createMilestone(milestone)

      return response.data
    },
    {
      onSuccess: () => {
        return Promise.all([queryClient.invalidateQueries(QueryKey.Milestones), queryClient.invalidateQueries(QueryKey.Milestone)])
      },
    },
  )
}

export const useUpdateMilestone = () => {
  return useMutation(
    async (milestone: MilestoneItem) => {
      const response = await workflowApi.updateMilestone(milestone.id, milestone)

      return response.data
    },
    {
      onSuccess: () => {
        return Promise.all([queryClient.invalidateQueries(QueryKey.Milestones), queryClient.invalidateQueries(QueryKey.Milestone)])
      },
    },
  )
}

export const useDeleteMilestone = () => {
  return useMutation(
    async (milestoneId: number) => {
      await workflowApi.deleteMilestone(milestoneId)
    },
    {
      onSuccess: () => {
        return Promise.all([queryClient.invalidateQueries(QueryKey.Milestones), queryClient.invalidateQueries(QueryKey.Milestone)])
      },
    },
  )
}

export const useUpdateMilestoneSequence = () => {
  return useMutation(
    async (milestoneList: MilestoneItem[]) => {
      await workflowApi.updateMilestoneSequence(milestoneList)
    },
    {
      onSuccess: () => {
        return Promise.all([queryClient.invalidateQueries(QueryKey.Milestones), queryClient.invalidateQueries(QueryKey.Milestone)])
      },
    },
  )
}

export const useCreateActivity = () => {
  return useMutation(
    async (milestone: Partial<MilestoneItem>) => {
      const response = await workflowApi.createActivity(milestone)

      return response.data
    },
    {
      onSuccess: () => {
        return Promise.all([queryClient.invalidateQueries(QueryKey.Milestones)])
      },
    },
  )
}

export const useGetChecklist = (objectId: number, milestoneId: number) => {
  return useQuery([QueryKey.Checklist, objectId, milestoneId], async (): Promise<Checklist> => {
    const response = await workflowApi.getChecklist(objectId, milestoneId)

    if (response.status === 'success') {
      return mapChecklistDto(response.data)
    } else {
      return {} as Checklist
    }
  })
}

export const useGetAllChecklists = (objectIds: number[]) => {
  return useQuery([QueryKey.Checklist, objectIds], async (): Promise<Checklist[]> => {
    const response = await workflowApi.getAllChecklists(objectIds)

    if (response.status === 'success') {
      return response.data.map((checklist) => mapChecklistDto(checklist))
    } else {
      return []
    }
  })
}

export const useSaveChecklist = () => {
  return useMutation(
    async (parameters: Omit<Checklist, 'id' | 'factStart' | 'factFinish'>) => {
      const response = await workflowApi.saveChecklist(mapChecklistSaveDto(parameters))

      return response.data
    },
    {
      onSuccess: () => {
        return Promise.all([queryClient.invalidateQueries(QueryKey.Checklist)])
      },
    },
  )
}

export const useUpdateChecklist = () => {
  return useMutation(
    async (parameters: Omit<Checklist, 'factStart' | 'factFinish'>) => {
      const response = await workflowApi.updateChecklist(parameters.id, parameters.objectId, parameters.milestoneId, parameters)

      return response.data
    },
    {
      onSuccess: () => {
        return Promise.all([queryClient.invalidateQueries(QueryKey.Checklist)])
      },
    },
  )
}

export const useStartChecklist = () => {
  return useMutation(
    async (parameters: { objectId: number; milestoneId: number }) => {
      const response = await workflowApi.startChecklist(parameters.objectId, parameters.milestoneId)

      return response.data
    },
    {
      onSuccess: () => {
        return Promise.all([queryClient.invalidateQueries(QueryKey.Checklist)])
      },
    },
  )
}

export const useSaveChecklistResponsibleByEmail = () => {
  return useMutation(
    async (parameters: { checklistId: number; responsibleEmail: string }) => {
      const response = await workflowApi.setChecklistResponsible(parameters.checklistId, parameters.responsibleEmail)

      return response.data
    },
    {
      onSuccess: () => {
        return Promise.all([queryClient.invalidateQueries(QueryKey.Checklist), queryClient.invalidateQueries(QueryKey.Objects)])
      },
    },
  )
}

export const useTransferMilestone = () => {
  return useMutation(async (dto: MilestoneTransferDto) => {
    const response = await workflowApi.transferMilestone(dto)

    return response.data
  })
}

export const useSaveFilter = () => {
  return useMutation(
    async (dto: Omit<MilestoneFilterTemplateDto, 'id'>) => {
      const response = await workflowApi.saveFilterTemplate(dto)

      return response.data
    },
    {
      onSuccess: () => {
        return Promise.all([queryClient.invalidateQueries([QueryKey.MilestoneFilter])])
      },
    },
  )
}

export const useLoadFilterTemplates = () => {
  return useQuery([QueryKey.MilestoneFilter], async () => {
    try {
      const response = await workflowApi.loadFilterTemplates()

      return response.data ?? []
    } catch {
      return []
    }
  })
}

export const useDeleteFilter = () => {
  return useMutation(
    async (filterId: number) => {
      await workflowApi.deleteFilterTemplate(filterId)
    },
    {
      onSuccess: () => {
        return Promise.all([queryClient.invalidateQueries([QueryKey.MilestoneFilter])])
      },
    },
  )
}

export const useGetLabelMilestoneSeq = (labelId?: number) => {
  return useQuery(
    [QueryKey.MilestoneSeq, labelId],
    async () => {
      const response = await workflowApi.getLabelMilestoneSeq(labelId ?? 0)

      return response.data
    },
    {
      enabled: !!labelId,
    },
  )
}

export const useUpdateLabelMilestoneSeq = () => {
  return useMutation(
    async (orderParams: FullMilestoneOrder) => {
      const { labelId, milestoneOrder } = orderParams
      await workflowApi.updateLabelMilestoneSeq(labelId, milestoneOrder)
    },
    {
      onSuccess: () => {
        return Promise.all([queryClient.invalidateQueries([QueryKey.MilestoneSeq])])
      },
    },
  )
}
